import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { X, Loader2, CalendarCheck2 } from 'lucide-react';
import { API } from '../../../lib/api';

const TYPES = [['detailing', 'Detailing / PPF studio'], ['bodyshop', 'Body shop'], ['multibrand', 'Multi-brand service'], ['showroom', 'Showroom with parts counter'], ['other', 'Something else']];
const EMPTY = { contact_name: '', workshop_name: '', city: '', phone: '', email: '', workshop_type: 'multibrand', bays: '', demo_slot: '', message: '' };
const fieldCls = 'w-full rounded-xl border border-[#d6d3cb] bg-[#ffffff] px-3.5 py-2.5 text-sm text-[#0f172a] outline-none focus:border-[#0f766e]';
const dayLabel = (iso) => new Date(`${iso}T12:00:00+05:30`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
const timeLabel = (s) => { const [h, m] = s.slice(11).split(':').map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`; };
export const errText = (e) => { const d = e?.response?.data?.detail; return Array.isArray(d) ? d.map((x) => x.msg.replace('Value error, ', '')).join('. ') : d || 'Could not book that. Please try again.'; };

const Input = ({ k, label, form, set, type = 'text', required = true }) => (
  <label className="block"><span className="mb-1 block text-xs font-semibold text-[#475569]">{label}</span>
    <input type={type} required={required} value={form[k]} onChange={(e) => set(k, e.target.value)} className={fieldCls} data-testid={`demo-${k}`} />
  </label>
);

export const SlotPicker = ({ days, form, set }) => {
  const [day, setDay] = useState('');
  useEffect(() => { if (days.length && !days.find((d) => d.date === day)) setDay(days[0].date); }, [days, day]);
  const slots = days.find((d) => d.date === day)?.slots || [];
  return (
    <div data-testid="demo-slot-picker">
      <span className="mb-1.5 block text-xs font-semibold text-[#475569]">Pick a time (IST, 30 minutes)</span>
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {days.map((d) => (
          <button key={d.date} type="button" onClick={() => setDay(d.date)} data-testid={`demo-day-${d.date}`}
            className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold ${day === d.date ? 'border-[#0f172a] bg-[#0f172a] text-[#f8fafc]' : 'border-[#d6d3cb] bg-[#ffffff] text-[#334155]'}`}>{dayLabel(d.date)}</button>
        ))}
      </div>
      <div className="mt-2 grid grid-cols-4 gap-1.5 sm:grid-cols-6">
        {slots.map((s) => (
          <button key={s} type="button" onClick={() => set('demo_slot', s)} data-testid={`demo-slot-${s}`}
            className={`rounded-lg border px-1 py-2 text-xs font-semibold ${form.demo_slot === s ? 'border-[#0f766e] bg-[#ccfbf1] text-[#134e4a]' : 'border-[#e2e0d8] bg-[#ffffff] text-[#334155] hover:border-[#0f766e]'}`}>{timeLabel(s)}</button>
        ))}
      </div>
    </div>
  );
};

const Done = ({ label, onClose }) => (
  <div className="py-10 text-center" data-testid="demo-success">
    <CalendarCheck2 className="mx-auto text-[#0f766e]" size={40} />
    <h3 className="dl-display mt-4 text-2xl font-semibold text-[#0f172a]">You’re booked</h3>
    <p className="mt-2 text-sm text-[#475569]">{label}. A confirmation with a calendar invite is on its way to your email. We will call you on your phone.</p>
    <button type="button" onClick={onClose} className="dl-btn dl-btn-ink mt-6 rounded-full px-6 py-3 text-sm font-semibold" data-testid="demo-done-btn">Done</button>
  </div>
);

let draft = EMPTY;

export const DemoDialog = ({ onClose }) => {
  const [form, setForm] = useState(() => ({ ...draft, demo_slot: '' }));
  const [days, setDays] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState('');
  const set = (k, v) => setForm((f) => { draft = { ...f, [k]: v }; return draft; });
  const loadSlots = () => axios.get(`${API}/public/driveline/demo-slots`).then((r) => setDays(r.data.days || [])).catch(() => {});
  useEffect(() => { loadSlots(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.demo_slot) { setErr('Pick a time for the demo.'); return; }
    setBusy(true); setErr('');
    try {
      const { data } = await axios.post(`${API}/public/driveline/demo`, form);
      setDone(data.slot_label);
      draft = EMPTY;
    } catch (ex) {
      setErr(errText(ex));
      if (ex?.response?.status === 409) { set('demo_slot', ''); loadSlots(); }
    } finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-[rgba(15,23,42,.55)] backdrop-blur-sm sm:items-center sm:p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()} data-testid="demo-dialog">
      <div className="dl-site max-h-[94vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl bg-[#f7f5f0] p-5 shadow-2xl sm:rounded-3xl sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="dl-display text-2xl font-semibold text-[#0f172a] sm:text-3xl">Book a 30-minute demo</h2>
            <p className="mt-1 text-sm text-[#475569]">We show Driveline on a workshop like yours, and answer every question.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 text-[#475569] hover:bg-[#e7e5df]" aria-label="Close" data-testid="demo-close"><X size={20} /></button>
        </div>
        {done ? <Done label={done} onClose={onClose} /> : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Input k="contact_name" label="Your name" form={form} set={set} />
              <Input k="workshop_name" label="Workshop name" form={form} set={set} />
              <Input k="city" label="City" form={form} set={set} />
              <Input k="phone" label="Phone" type="tel" form={form} set={set} />
              <Input k="email" label="Email" type="email" form={form} set={set} />
              <label className="block"><span className="mb-1 block text-xs font-semibold text-[#475569]">Bays / branches</span>
                <input value={form.bays} onChange={(e) => set('bays', e.target.value)} placeholder="e.g. 6 bays, 1 branch" className={fieldCls} data-testid="demo-bays" />
              </label>
            </div>
            <label className="block"><span className="mb-1 block text-xs font-semibold text-[#475569]">Workshop type</span>
              <select value={form.workshop_type} onChange={(e) => set('workshop_type', e.target.value)} className={fieldCls} data-testid="demo-workshop_type">
                {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <SlotPicker days={days} form={form} set={set} />
            <label className="block"><span className="mb-1 block text-xs font-semibold text-[#475569]">What do you want to see? (optional)</span>
              <textarea rows={2} value={form.message} onChange={(e) => set('message', e.target.value)} className={fieldCls} data-testid="demo-message" />
            </label>
            {err && <p className="rounded-xl border border-[#fecaca] bg-[#fef2f2] px-3 py-2 text-sm text-[#b91c1c]" data-testid="demo-error">{err}</p>}
            <button type="submit" disabled={busy} className="dl-btn dl-btn-ink inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[15px] font-semibold disabled:opacity-60" data-testid="demo-submit">
              {busy && <Loader2 size={16} className="animate-spin" />} Book my demo
            </button>
          </form>
        )}
      </div>
    </div>, document.body);
};

export default DemoDialog;
